// Utility functions for consistent formatting across all weld tables

// Timezone conversion functions
const getTimezoneFromSettings = (): string => {
  try {
    const settings = localStorage.getItem('settings');
    if (settings) {
      const settingsData = JSON.parse(settings);
      return settingsData.general?.timeZone || 'utc';
    }
  } catch (error) {
  }
  return 'utc'; // Default fallback
};

// Convert timestamp to display timezone
const convertTimestampToTimezone = (timestamp: number | string): Date => {
  const timezone = getTimezoneFromSettings();
  const date = new Date(timestamp);
  
  switch (timezone) {
    case 'est':
      // Convert FROM UTC TO Eastern Time (UTC-5)
      return new Date(date.getTime() - (5 * 60 * 60 * 1000));
    case 'pst':
      // Convert FROM UTC TO Pacific Time (UTC-8)
      return new Date(date.getTime() - (8 * 60 * 60 * 1000));
    case 'utc':
    default:
      // Keep as UTC
      return date;
  }
};

// Get timezone display name
export const getTimezoneDisplayName = (): string => {
  const timezone = getTimezoneFromSettings();
  
  switch (timezone) {
    case 'est':
      return 'Eastern Time (EST)';
    case 'pst':
      return 'Pacific Time (PST)';
    case 'utc':
    default:
      return 'UTC';
  }
};

export interface AnalyticsDateTimeParts {
  date: string;
  time: string;
  full: string;
}

/**
 * Format a millisecond timestamp for analytics UI: "27 Apr 2025" + time.
 * Appends " UTC" (or EST/PST) when that timezone is active in user settings.
 */
export const formatAnalyticsDateTime = (timestampMs?: number | null): AnalyticsDateTimeParts => {
  if (!timestampMs) {
    return { date: '—', time: '—', full: '—' };
  }
  try {
    const tz = getTimezoneFromSettings();
    const date = convertTimestampToTimezone(timestampMs);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-GB', { month: 'short' });
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const tzSuffix = tz === 'utc' ? ' UTC' : tz === 'est' ? ' EST' : tz === 'pst' ? ' PST' : '';
    const dateStr = `${day} ${month} ${year}`;
    const timeStr = `${displayHours}:${minutes} ${ampm}${tzSuffix}`;
    return { date: dateStr, time: timeStr, full: `${dateStr}, ${timeStr}` };
  } catch {
    return { date: '—', time: '—', full: '—' };
  }
};

/**
 * Format time for display with timezone conversion (consistent with unitUtils.ts)
 * @param timeString - ISO string or timestamp
 * @returns Formatted time string like "25-Jan-2025, 2:30 PM"
 */
export const formatTimeForDisplay = (timeString?: string | number) => {
  if (!timeString) return '-';
  
  try {
    const convertedDate = convertTimestampToTimezone(timeString);
    const day = String(convertedDate.getDate()).padStart(2, '0');
    const month = convertedDate.toLocaleDateString('en-US', { month: 'short' });
    const year = convertedDate.getFullYear();
    const hours = convertedDate.getHours();
    const minutes = String(convertedDate.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${day}-${month}-${year}, ${displayHours}:${minutes} ${ampm}`;
  } catch (error) {
    return '-';
  }
};

/**
 * Format duration from minutes to "Xm Ys" format
 * @param durationString - Duration string like "2.56 mins" or "2.56"
 * @returns Formatted duration like "2m 17s"
 */
export const formatDuration = (durationString?: string | number) => {
  if (!durationString) return '-';
  
  try {
    let totalMinutes: number;
    
    if (typeof durationString === 'number') {
      totalMinutes = durationString;
    } else {
      // Extract minutes from "2.56 mins" format
      const minutesMatch = durationString.match(/(\d+\.?\d*)\s*mins?/);
      if (minutesMatch) {
        totalMinutes = parseFloat(minutesMatch[1]);
      } else {
        // Try to parse as direct number
        totalMinutes = parseFloat(durationString);
      }
    }
    
    if (isNaN(totalMinutes)) return durationString;
    
    const minutes = Math.floor(totalMinutes);
    const seconds = Math.round((totalMinutes - minutes) * 60);
    
    if (seconds === 0) {
      return `${minutes}m`;
    } else {
      return `${minutes}m ${seconds}s`;
    }
  } catch (error) {
    return durationString;
  }
};

/**
 * Format duration when the API value is stored in seconds (e.g. weld-segment arc_time).
 * Does not change {@link formatDuration}, which expects minutes.
 * @param durationSeconds - Total seconds (number or numeric string)
 * @returns Formatted duration like "2m 17s" or "43s"
 */
export const formatDurationFromSeconds = (durationSeconds?: string | number | null) => {
  if (durationSeconds === null || durationSeconds === undefined || durationSeconds === '') {
    return '-';
  }

  try {
    const totalSeconds =
      typeof durationSeconds === 'number' ? durationSeconds : parseFloat(String(durationSeconds).trim());

    if (!Number.isFinite(totalSeconds)) return String(durationSeconds);

    const roundedTotal = Math.max(0, Math.round(totalSeconds));
    let minutes = Math.floor(roundedTotal / 60);
    let seconds = roundedTotal % 60;

    if (seconds === 60) {
      minutes += 1;
      seconds = 0;
    }

    if (minutes === 0) {
      return `${seconds}s`;
    }
    if (seconds === 0) {
      return `${minutes}m`;
    }
    return `${minutes}m ${seconds}s`;
  } catch {
    return String(durationSeconds);
  }
};

/**
 * Format timestamp for display with timezone conversion (consistent with unitUtils.ts)
 * @param timestamp - Unix timestamp in seconds
 * @returns Formatted time string like "25-Jan-2025, 2:30 PM"
 */
export const formatTimestamp = (timestamp?: number) => {
  if (!timestamp) return '-';
  // Convert Unix timestamp from seconds to milliseconds for formatTimeForDisplay
  return formatTimeForDisplay(timestamp * 1000);
};

export interface WeldStartEndRangeParts {
  dateLine: string;
  timeLine: string;
  full: string;
}

/**
 * Production-style weld start/end display for drill-down tables.
 * Same-day: "18-Sep-2025" + "8:23 PM → 9:08 PM"
 */
export function formatWeldStartEndRange(
  startMs?: number | null,
  endMs?: number | null,
): WeldStartEndRangeParts {
  if (!startMs && !endMs) {
    return { dateLine: '—', timeLine: '—', full: '—' };
  }
  const startStr = startMs ? formatTimestamp(startMs / 1000) : '—';
  const endStr = endMs ? formatTimestamp(endMs / 1000) : '—';
  if (startStr === '—' && endStr === '—') {
    return { dateLine: '—', timeLine: '—', full: '—' };
  }
  const [startDate, startTime] = startStr.includes(', ') ? startStr.split(', ') : [startStr, ''];
  const [endDate, endTime] = endStr.includes(', ') ? endStr.split(', ') : [endStr, ''];
  if (startDate === endDate && startTime && endTime) {
    return {
      dateLine: startDate,
      timeLine: `${startTime} → ${endTime}`,
      full: `${startDate}\n${startTime} → ${endTime}`,
    };
  }
  return {
    dateLine: startStr,
    timeLine: endStr !== '—' ? `→ ${endStr}` : '—',
    full: `${startStr} → ${endStr}`,
  };
}

/**
 * Format decimal numbers with consistent decimal places
 * @param value - Number to format
 * @param decimals - Number of decimal places
 * @returns Formatted number string
 */
export const formatDecimal = (value?: number | null, decimals: number = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return value.toFixed(decimals);
};

/**
 * Format time for display showing only time with seconds (timezone-aware)
 * @param timeString - ISO string or timestamp
 * @returns Formatted time string like "9:37:16 PM"
 */
export const formatTimeOnly = (timeString?: string | number) => {
  if (!timeString) return '-';
  
  try {
    const convertedDate = convertTimestampToTimezone(timeString);
    const hours = convertedDate.getHours();
    const minutes = String(convertedDate.getMinutes()).padStart(2, '0');
    const seconds = String(convertedDate.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes}:${seconds} ${ampm}`;
  } catch (error) {
    return '-';
  }
};

/**
 * Format date for display showing only date (timezone-aware)
 * @param timeString - ISO string or timestamp
 * @returns Formatted date string like "25-Jan-2025"
 */
export const formatDateOnly = (timeString?: string | number) => {
  if (!timeString) return '-';
  
  try {
    const convertedDate = convertTimestampToTimezone(timeString);
    const day = String(convertedDate.getDate()).padStart(2, '0');
    const month = convertedDate.toLocaleDateString('en-US', { month: 'short' });
    const year = convertedDate.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (error) {
    return '-';
  }
};

/**
 * Format date and time for display (timezone-aware)
 * @param timeString - ISO string or timestamp
 * @returns Formatted date and time string like "25-Jan-2025, 2:30:45 PM"
 */
export const formatDateTime = (timeString?: string | number) => {
  if (!timeString) return '-';
  
  try {
    const convertedDate = convertTimestampToTimezone(timeString);
    const day = String(convertedDate.getDate()).padStart(2, '0');
    const month = convertedDate.toLocaleDateString('en-US', { month: 'short' });
    const year = convertedDate.getFullYear();
    const hours = convertedDate.getHours();
    const minutes = String(convertedDate.getMinutes()).padStart(2, '0');
    const seconds = String(convertedDate.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${day}-${month}-${year}, ${displayHours}:${minutes}:${seconds} ${ampm}`;
  } catch (error) {
    return '-';
  }
}; 